import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { resolveSubscription, projectedStepCount, type ProductKey } from '@/router/products'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'

/**
 * Lit l'état du parcours depuis l'URL (source de vérité unique).
 * Le séquencement est conditionnel (cf. resolveSubscription) : chaque étape suivante
 * dépend des réponses déjà collectées.
 * - answerPathSlugs : segments bruts après le slug produit
 * - answers        : map décodée stepKey → valeur
 * - sequence       : étapes effectivement traversées (répondues + courante)
 * - currentStepKey : prochaine étape à répondre (null si toutes répondues)
 * - currentStepIndex : numéro 1-based pour le stepper
 * - totalSteps     : nombre d'étapes du parcours projeté (pour le stepper)
 */
export function useSubscriptionState() {
  const route = useRoute()

  const locale = computed<Locale>(
    () => (route.meta.locale ?? DEFAULT_LOCALE) as Locale,
  )

  /** Produit courant : sélectionne l'arbre de décision (cf. nextStep). */
  const product = computed<ProductKey | undefined>(() => route.meta.productKey)

  const answerPathSlugs = computed<string[]>(() => {
    const raw = route.params.answerPath
    if (Array.isArray(raw)) return raw as string[]
    if (typeof raw === 'string' && raw.length) return [raw]
    return []
  })

  const resolved = computed(() => resolveSubscription(answerPathSlugs.value, locale.value, product.value))

  const answers = computed(() => resolved.value.answers)
  const sequence = computed(() => resolved.value.sequence)
  const currentStepKey = computed(() => resolved.value.currentStepKey)
  const currentStepIndex = computed(() => answerPathSlugs.value.length + 1)
  const totalSteps = computed(() => projectedStepCount(answers.value, product.value))

  return { locale, product, answerPathSlugs, answers, sequence, currentStepKey, currentStepIndex, totalSteps }
}
